import { formatDateKey, parseDateTime } from '@/js/core/utils/formatters';
import {
  DEFAULT_TIMEZONE,
  formatTimezoneLabel,
  getZonedParts,
  normalizeTimezone,
  shouldShowTimezoneLabel,
} from '@/js/core/utils/timezone';

const DAY_ORDER = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Normalization is pure per (raw object, timezone), but the views re-map the
// whole accumulated list on every page append. Memoize on raw object identity
// (store appends preserve item references) so only new pages pay the cost.
const normalizeCache = new WeakMap();

function memoizedNormalize(rawObject, timezone, compute) {
  if (!rawObject || typeof rawObject !== 'object') return compute();

  const cached = normalizeCache.get(rawObject);

  if (cached && cached.timezone === timezone) return cached.value;

  const value = compute();
  normalizeCache.set(rawObject, { timezone, value });

  return value;
}

function weekdayLabelInTimezone(startTimeUtc, timezone) {
  const date = parseDateTime(startTimeUtc, 'UTC');
  if (!date) return '';

  return getZonedParts(date, normalizeTimezone(timezone), { weekday: 'short' }).weekday || '';
}

export function slugifyText(text) {
  if (!text) return '';
  return String(text)
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function facultyLastNames(faculty = []) {
  if (!Array.isArray(faculty)) return '';

  return faculty
    .map(profile => {
      const name = profile?.identity?.name || {};
      return name.last || name.full || name.display || '';
    })
    .filter(Boolean)
    .join(', ');
}

export function normalizeMiniProfile(profile = {}) {
  const safeProfile = profile || {};
  const name = safeProfile.identity?.name || {};
  const affiliation = safeProfile.affiliation || {};
  const title = affiliation.title || '';
  const department = affiliation.department || '';

  return {
    uuid: safeProfile.uuid || '',
    firstName: name.first || '',
    lastName: name.last || '',
    displayName: name.full || name.display || '',
    department: department ? [department] : [],
    titles: title ? [title] : [],
    status: affiliation.audience_group || '',
    email: safeProfile.identity?.email || '',
  };
}

export function courseScheduleLocations(schedule = []) {
  if (!Array.isArray(schedule)) return [];

  const locations = schedule
    .map(item => (typeof item?.location === 'string' ? item.location.trim() : ''))
    .filter(Boolean);

  return Array.from(new Set(locations));
}

export function normalizeCourse(course = {}, timezone = DEFAULT_TIMEZONE) {
  return memoizedNormalize(course, timezone, () => buildNormalizedCourse(course, timezone));
}

function buildNormalizedCourse(course = {}, timezone = DEFAULT_TIMEZONE) {
  const departments = Array.isArray(course.departments)
    ? course.departments.filter(Boolean)
    : [course.departments].filter(Boolean);

  const faculty = Array.isArray(course.faculty_mini_profiles)
    ? course.faculty_mini_profiles.map(normalizeMiniProfile)
    : (course.faculty || []).map(normalizeMiniProfile);

  const rawSessions = course.sessions || [];
  const sessions = rawSessions.map(session => normalizeCourseSession(session, timezone));

  const activeDays = Array.from(
    new Set(
      (course.weekday_indicators || [])
        .filter(day => day.active)
        .map(day => weekdayLabelInTimezone(day.start_time_utc, timezone) || day.label)
    )
  ).sort((a, b) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b));

  const scheduleLocations = courseScheduleLocations(course.schedule);
  const scheduleTimezoneLabel = formatTimezoneLabel(new Date(), timezone);
  const timezoneLabel = shouldShowTimezoneLabel(timezone) ? scheduleTimezoneLabel : '';

  return {
    id: course.uuid || course.source_api_id || course.slug || '',
    uuid: course.uuid || '',
    slug: course.slug || '',
    sourceApiId: course.source_api_id || '',
    number: course.course_number || '',
    name: course.title || '',
    term: course.term || '',
    year: course.year || '',
    type: course.course_type || '',
    department: departments.join(', '),
    departments,
    units: course.units ?? null,
    links: normalizeCourseLinks(course.links || [], course.website_url),
    faculty,
    facultyLastNames: facultyLastNames(course.faculty_mini_profiles || course.faculty || []),
    days: activeDays,
    timezoneLabel,
    scheduleTimezoneLabel,
    sessions,
    description: course.description || '',
    location: scheduleLocations.join(', '),
    schedule: course.schedule || [],
    termLabel: [capitalize(course.term), course.year].filter(Boolean).join(' '),
  };
}

export function normalizeCourseSession(session = {}, timezone = DEFAULT_TIMEZONE) {
  const startDate = parseDateTime(session.start_time_utc, 'UTC');
  const endDate = parseDateTime(session.end_time_utc, 'UTC');

  return {
    id: session.uuid || session.source_event_id || '',
    uuid: session.uuid || '',
    sourceEventId: session.source_event_id || '',
    courseNumber: session.course_number || '',
    number: session.course_number || '',
    name: session.title || '',
    type: session.category || '',
    date: formatCourseDateKey(session.start_time_utc, timezone),
    time: formatCourseClock(startDate, timezone),
    endTime: formatCourseClock(endDate, timezone),
    startTimeUtc: session.start_time_utc || '',
    endTimeUtc: session.end_time_utc || '',
    location: session.location || '',
    locationUrl: session.location_url || '',
    registrationLink: session.registration_link || '',
    zoomLink: session.zoom_link || '',
    sortTime: startDate ? startDate.getTime() : 0,
  };
}

export function normalizeUpcomingGroups(upcomingSessions = [], timezone = DEFAULT_TIMEZONE) {
  const sessions = upcomingSessions
    .map(session => normalizeUpcomingSession(session, timezone))
    .sort(
      // Tie-break on name then courseNumber to mirror the server's ORDER BY, so
      // appended pages keep their positions instead of reshuffling within a time slot
      (a, b) =>
        a.date.localeCompare(b.date) ||
        a.sortTime - b.sortTime ||
        a.name.localeCompare(b.name) ||
        a.courseNumber.localeCompare(b.courseNumber)
    );
  const groups = new Map();

  sessions.forEach(session => {
    if (!groups.has(session.date)) {
      groups.set(session.date, {
        date: session.date,
        label: formatUpcomingDateLabel(session.date),
        sessions: [],
      });
    }

    groups.get(session.date).sessions.push(session);
  });

  return Array.from(groups.values());
}

export function normalizeUpcomingSession(session = {}, timezone = DEFAULT_TIMEZONE) {
  return memoizedNormalize(session, timezone, () =>
    buildNormalizedUpcomingSession(session, timezone)
  );
}

function buildNormalizedUpcomingSession(session = {}, timezone = DEFAULT_TIMEZONE) {
  const course = normalizeCourse(session.course || {}, timezone);
  const normalizedSession = normalizeCourseSession(session, timezone);

  return {
    ...normalizedSession,
    slug: course.slug,
    name: course.name || normalizedSession.name,
    type: course.type || normalizedSession.type,
    course,
    facultyLastNames: course.facultyLastNames,
    timezoneLabel: course.timezoneLabel,
  };
}

export function normalizeCourseLinks(links = [], websiteUrl = '') {
  const normalizedLinks = Array.isArray(links) ? links : [];

  return normalizedLinks
    .map(link => ({
      label: link.label || '',
      url: link.url || '',
      type: slugifyText(link.label || ''),
    }))
    .filter(link => link.label && link.url)
    .concat(
      normalizedLinks.length === 0 && websiteUrl
        ? [{ label: 'Course Website', url: websiteUrl, type: 'course-website' }]
        : []
    );
}

// Maps the filter-group selections to /courses query params; 'all' means no constraint.
export function coursesQueryFromFilters(selectedFilters = {}) {
  const pick = groupId => {
    const values = selectedFilters?.[groupId];

    if (!Array.isArray(values) || values.includes('all')) return [];

    // Copy: the live selection array is mutated in place by the filter UI, and the
    // store keeps the returned query as its change-detection baseline.
    return [...values];
  };

  return {
    alphabet: pick('alphabet'),
    course_type: pick('course-types'),
    department: pick('departments'),
  };
}

export function groupSessionsByWeek(sessions) {
  const byWeek = new Map();

  for (const session of sessions) {
    const date = new Date(`${session.date}T00:00:00`);
    const dayOfWeek = date.getDay();
    const offsetToMonday = (dayOfWeek + 6) % 7;
    const monday = new Date(date);
    monday.setDate(date.getDate() - offsetToMonday);
    const key = monday.toISOString().slice(0, 10);
    if (!byWeek.has(key)) byWeek.set(key, []);
    byWeek.get(key).push(session);
  }

  return Array.from(byWeek.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([, items]) => items);
}

export function timeRangeParts(start, end) {
  const startTime = parseClockTime(start);
  const endTime = parseClockTime(end);
  if (!startTime) return { start: start || '', end: '' };
  if (!endTime) return { start: `${formatHourMinute(startTime)} ${startTime.period}`, end: '' };

  if (startTime.period === endTime.period) {
    return {
      start: formatHourMinute(startTime),
      end: `${formatHourMinute(endTime)} ${endTime.period}`,
    };
  }

  return {
    start: `${formatHourMinute(startTime)} ${startTime.period}`,
    end: `${formatHourMinute(endTime)} ${endTime.period}`,
  };
}

export function scheduleLineParts(session) {
  const date = new Date(`${session.date}T00:00:00`);
  const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const dayOfMonth = date.getDate();
  const time = timeRangeParts(session.time, session.endTime);

  return {
    start: `${weekday}, ${month} ${dayOfMonth}, ${time.start}`,
    end: time.end || '',
  };
}

export function formatCourseClock(date, timezone = DEFAULT_TIMEZONE) {
  if (!date) return '';

  const parts = getZonedParts(date, normalizeTimezone(timezone));
  const hours24 = Number(parts.hour);
  const minutes = Number(parts.minute);
  const hours = hours24 % 12 || 12;
  const minuteText = String(minutes).padStart(2, '0');
  const period = hours24 >= 12 ? 'pm' : 'am';

  return `${hours}:${minuteText} ${period}`;
}

export function formatCourseDateKey(dateString, timezone = DEFAULT_TIMEZONE) {
  return formatDateKey(dateString, timezone, 'UTC');
}

export function formatUpcomingDateLabel(dateKey) {
  const date = new Date(`${dateKey}T00:00:00`);
  const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
  const monthDay = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  return `${weekday}, ${monthDay}`;
}

export function capitalize(value) {
  const text = String(value || '');

  return text ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : '';
}

export function parseClockTime(timeStr) {
  if (!timeStr) return null;
  const match = timeStr.trim().match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)$/i);
  if (!match) return null;

  return {
    hour: parseInt(match[1], 10),
    minute: parseInt(match[2] || '0', 10),
    period: match[3].toLowerCase(),
  };
}

export function formatHourMinute(time) {
  return `${time.hour}:${String(time.minute).padStart(2, '0')}`;
}
